import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import RunResultView from './RunResultView';

/**
 * A run paused at a human-input step: shows what to review and collects the answer.
 * `onDecide({approved?, text})` continues the run.
 */
export default function ApprovalCard({
  approval, onDecide, busy = false, error = null,
}) {
  const [text, setText] = useState('');
  const type = approval?.inputType || 'approval';
  const choices = Array.isArray(approval?.choices) ? approval.choices : [];
  const canDecide = approval?.canDecide !== false;
  const hasPreview = approval?.preview != null && approval.preview !== '';

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: 3, borderColor: 'warning.main' }}
      data-testid="flow-approval"
      data-approval-id={approval?.id || ''}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <HourglassTopIcon sx={{ color: 'warning.main' }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Waiting for a person</Typography>
        {approval?.source === 'trigger' && <Chip size="small" variant="outlined" label="From a trigger" />}
        {approval?.source === 'mcp' && <Chip size="small" variant="outlined" label="From an AI assistant" />}
      </Stack>
      <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }} data-testid="flow-approval-prompt">
        {approval?.prompt || 'Please review and reply'}
      </Typography>
      {hasPreview && (
        <Box
          sx={{
            bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 1.5, maxHeight: 360, overflow: 'auto',
          }}
          data-testid="flow-approval-preview"
        >
          <RunResultView value={approval.preview} />
        </Box>
      )}
      {!canDecide ? (
        <Alert severity="info">Only the flow owner or a workspace admin can answer this.</Alert>
      ) : (
        <>
          {type === 'choice' && choices.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
              {choices.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  color={text === c ? 'primary' : 'default'}
                  variant={text === c ? 'filled' : 'outlined'}
                  onClick={() => setText(c)}
                  disabled={busy}
                />
              ))}
            </Stack>
          )}
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={type === 'approval' ? 1 : 2}
            label={type === 'approval' ? 'Comment (optional)' : 'Your reply'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
            inputProps={{ 'data-testid': 'flow-approval-text' }}
          />
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            {type === 'approval' ? (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => onDecide({ approved: true, text })}
                  disabled={busy}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                  data-testid="flow-approval-approve"
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => onDecide({ approved: false, text })}
                  disabled={busy}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  data-testid="flow-approval-reject"
                >
                  Reject
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => onDecide({ text })}
                disabled={busy || !text.trim()}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 700 }}
                data-testid="flow-approval-send"
              >
                Send reply
              </Button>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
}
